import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import { auth } from '@/firebase/config'
import getUser from '@/composables/getUser'

// auth guards
const requireAuth = (to, from, next) => {
  const user = auth.currentUser
  // console.log('current user in auth guard: ', user)
  if (!user) {
    next({ name: 'Welcome' })
  } else {
    next()
  }
}

const requireNoAuth = (to, from, next) => {
  const user = auth.currentUser
  if (user) {
    next()
  } else {
    next()
  }
}

const beforeEntering = async (to, from) => {
  const { user, role } = await getUser()
  // console.log(user, role.value)
  // const canAccess = await canUserAccess(to)
  if (!user) return '/'
  if (user && role.value === 'testAdmin') return
  if (user && role.value === 'teacher') return '/teacher'
  if (user && role.value === 'learner') return '/class'
  // reject the navigation
  return false
}

const beforeTeacher = async (to, from) => {
  const { user, role } = await getUser()
  if (!user) return '/'
  if (user && role.value === 'testAdmin') return
  if (user && role.value === 'teacher') return
  if (user && role.value === 'learner') return '/class'
  // reject the navigation
  return false
}

const beforeLearner = async (to, from) => {
  const { user, role } = await getUser()
  if (!user) return '/'
  if (user && role.value === 'testAdmin') return
  if (user && role.value === 'teacher') return '/teacher'
  if (user && role.value === 'learner') return
  // reject the navigation
  return false
}

const routes = [
  {
    path: '/',
    name: 'Home',
    meta: {
      title: 'Home-Inspire Leadership School'
    },
    component: Home
  },
  {
    path: '/school',
    name: 'OurSchool',
    meta: {
      title: 'Our School-Inspire Leadership School'
    },
    component: () => import(/* webpackChunkName: "school" */ '../views/OurSchool.vue')
  },
  {
    path: '/contact',
    name: 'Contact',
    meta: {
      title: 'Contact Us-Inspire Leadership School'
    },
    component: () => import(/* webpackChunkName: "contact" */ '../views/Contact.vue')
  },
  {
    path: '/super',
    name: 'Super',
    component: () => import(/* webpackChunkName: "super" */ '../views/Super.vue')
  },
  {
    path: '/administration',
    name: 'Administration',
    component: () => import(/* webpackChunkName: "administration" */ '../views/Administration.vue'),
    meta: {
      title: 'Admin-Inspire Leadership School'
    },
    beforeEnter: [requireAuth, beforeEntering]
  },
  {
    path: '/teacher',
    name: 'Teacher',
    component: () => import(/* webpackChunkName: "Teacher" */ '../views/Teacher.vue'),
    meta: {
      title: 'Teacher-Inspire Leadership School'
    },
    beforeEnter: [requireAuth, beforeTeacher]
  },
  {
    path: '/class',
    name: 'Class',
    component: () => import(/* webpackChunkName: "class" */ '../views/classes/Classes.vue'),
    meta: {
      title: 'Class-Inspire Leadership School'
    },
    beforeEnter: [requireAuth, beforeLearner]
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import(/* webpackChunkName: "profile" */ '../views/classes/Profile.vue'),
    meta: {
      title: 'Profile-Inspire Leadership School'
    },
    beforeEnter: [requireAuth, beforeLearner]
  },
  {
    path: '/policies',
    name: 'Parents',
    component: () => import(/* webpackChunkName: "parents" */ '../views/documents/Parents.vue'),
    props: true,
    meta: {
      title: 'Policies-Inspire Leadership School'
    },
    beforeEnter: requireAuth
  },
  {
    path: '/staff',
    name: 'Staff',
    component: () => import(/* webpackChunkName: "staff" */ '../views/documents/Staff.vue'),
    props: true,
    meta: {
      title: 'Staff Policies-Inspire Leadership School'
    },
    beforeEnter: requireAuth
  },
  {
    path: '/events',
    name: 'Events',
    component: () => import(/* webpackChunkName: "gallery" */ '../views/activities/Gallery.vue'),
    props: true,
    meta: {
      title: 'Events-Inspire Leadership School'
    },
    beforeEnter: requireAuth
  },
  {
    path: '/circulars',
    name: 'Circulars',
    component: () => import(/* webpackChunkName: "circulars" */ '../views/activities/Circular.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/circulars/:id',
    name: 'CircularDetails',
    component: () => import(/* webpackChunkName: "circularDetails" */ '../views/activities/CircularDetails.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/extracurricular',
    name: 'Clubs',
    component: () => import(/* webpackChunkName: "clubs" */ '../views/activities/Club.vue')
  },
  {
    path: '/policies/bank-details',
    name: 'Bank',
    component: () => import(/* webpackChunkName: "bank" */ '../views/documents/BankDetails.vue')
  },
  {
    path: '/policies/termly-requirements',
    name: 'TermReq',
    component: () => import(/* webpackChunkName: "termreq" */ '../views/documents/TermReq.vue')
  },
  {
    path: '/policies/fees',
    name: 'Fees',
    component: () => import(/* webpackChunkName: "fees" */ '../views/documents/Fees.vue')
  },
  {
    path: '/policies/schoolbus',
    name: 'SchoolBus',
    component: () => import(/* webpackChunkName: "schoolbus" */ '../views/documents/SchoolBus.vue')
  },
  {
    path: '/policies/refund',
    name: 'Refund',
    component: () => import(/* webpackChunkName: "refund" */ '../views/documents/Refund.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/policies/meals',
    name: 'Meals',
    component: () => import(/* webpackChunkName: "meals" */ '../views/documents/Meals.vue')
  },
  {
    path: '/policies/conduct',
    name: 'Conduct',
    component: () => import(/* webpackChunkName: "conduct" */ '../views/documents/Conduct.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/policies/homework',
    name: 'Homework',
    component: () => import(/* webpackChunkName: "homework" */ '../views/documents/Homework.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/policies/chartdisplay',
    name: 'ChartDisplay',
    component: () => import(/* webpackChunkName: "chartdisplay" */ '../views/documents/ChartDisplay.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/message/:id',
    name: 'Messaging',
    component: () => import(/* webpackChunkName: "messaging" */ '../views/classes/Messaging.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  {
    path: '/welcome',
    name: 'Welcome',
    component: () => import(/* webpackChunkName: "welcome" */ '../views/Welcome.vue'),
    beforeEnter: requireNoAuth
  },
  {
    path: '/announcements',
    name: 'Announcements',
    component: () => import(/* webpackChunkName: "announcements" */ '../views/announcements/Announcements.vue'),
    beforeEnter: requireAuth
  },
  {
    path: '/announcements/:id',
    name: 'AnnouncementDetails',
    component: () => import(/* webpackChunkName: "announcementDetails" */ '../views/announcements/AnnouncementDetails.vue'),
    props: true,
    beforeEnter: requireAuth
  },
  // catchall
  {
    path: '/:catchAll(.*)',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "404" */ '../views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach((to, from, next) => {
  document.title = `${to.meta.title}`
  next()
})
export default router
